import {prependContainerName} from '@packages/systems/plugins/Frame';
import {Breakpoint} from '@packages/systems/style/media-queries';
import {BreakpointsMap} from '@packages/systems/style/types';
import {WEBFLOW_SITE_IFRAME_ID} from '@packages/systems/core/constants/SharedConfig';
import {reportBug} from '@packages/utilities/reporter';
import {rewriteViewportUnits} from '@packages/systems/open-canvas/rewrite-viewport-units';
import {rewriteBodySelectors} from './rewriteBodySelectors';

/**
 * Replaces any `@media` rules in the component canvas with equivalent
 * `@container` rules.
 *
 * NOTE: Stylesheets marked with `data-wf-builtin-stylesheet` are excluded, since
 * they already hand-author their own `@container` rules alongside their `@media`
 * rules. Those `@media` rules are instead handled by
 * {@link disableMediaRulesWithExistingContainerEquivalent}, when
 * `mut_disabledBuiltinMediaRules` is passed in.
 *
 * Concretely, something like this:
 *
 * ```css
 * @media (width > 42px) {
 *   .foo {
 *     color: red;
 *   }
 * }
 * ```
 *
 * ...becomes this:
 *
 * ```css
 * @container wf-container-medium (width > 42px) {
 *   .foo {
 *     color: red;
 *   }
 * }
 * ```
 */
export function replaceComponentCanvasMediaQueries({
  breakpoints,
  bodyElementStyleTarget,
  mut_disabledBuiltinMediaRules,
}: {
  breakpoints: BreakpointsMap<Breakpoint>;
  bodyElementStyleTarget?: string | null;
  mut_disabledBuiltinMediaRules?: Map<CSSMediaRule, string>;
}) {
  const containerNames = new Set(
    Object.values(breakpoints).map(getBreakpointContainerName)
  );

  const iFrame = document.getElementById(WEBFLOW_SITE_IFRAME_ID);
  if (!(iFrame instanceof HTMLIFrameElement)) return 'Iframe not found';

  const doc = iFrame.contentDocument;
  if (!doc) return 'No document found in iframe';

  for (const sheet of doc.styleSheets) {
    if (isWebflowStylesheet(sheet)) continue;
    if (isBuiltinStylesheet(sheet)) {
      if (mut_disabledBuiltinMediaRules) {
        disableMediaRulesWithExistingContainerEquivalent(
          sheet,
          mut_disabledBuiltinMediaRules
        );
      }
      continue;
    }
    mediaToContainerQueries(sheet, containerNames, bodyElementStyleTarget);
    rewriteViewportUnitsInSheet(sheet);
  }
}

/**
 * Rewrites `@media` → `@container` in code component shadow root stylesheets.
 *
 * Processes any already-loaded shadow root sheets immediately, then listens
 * for `wf:code-island-stylesheet-loaded` custom events dispatched by
 * `CodeIslandCustomElement.mountStyles` when `<link>` stylesheets finish
 * loading.
 *
 * Returns a cleanup function, or `undefined` if setup failed.
 */
export function replaceCodeIslandShadowDomMediaQueries({
  breakpoints,
}: {
  breakpoints: BreakpointsMap<Breakpoint>;
}): (() => void) | undefined {
  const containerNames = new Set(
    Object.values(breakpoints).map(getBreakpointContainerName)
  );

  const iFrame = document.getElementById(WEBFLOW_SITE_IFRAME_ID);
  if (!(iFrame instanceof HTMLIFrameElement)) return;

  const doc = iFrame.contentDocument;
  if (!doc) return;

  const processedSheets = new WeakSet<CSSStyleSheet>();

  const processStylesheet = (sheet: CSSStyleSheet) => {
    if (processedSheets.has(sheet)) return;
    if (isWebflowStylesheet(sheet)) return;
    if (isBuiltinStylesheet(sheet)) return;
    try {
      mediaToContainerQueries(sheet, containerNames);
      rewriteViewportUnitsInSheet(sheet);
      processedSheets.add(sheet);
    } catch {
      // Stylesheet may not be accessible (cross-origin)
    }
  };

  // Process any shadow root sheets that are already loaded (covers the case
  // where this observer is recreated after a breakpoint change — the custom
  // events already fired and won't fire again).
  for (const island of doc.querySelectorAll('code-island')) {
    const shadowRoot = island.shadowRoot;
    if (!shadowRoot) continue;
    try {
      for (const sheet of shadowRoot.styleSheets) {
        processStylesheet(sheet);
      }
    } catch {
      // Shadow root stylesheets may not be accessible
    }
  }

  // Listen for the custom event dispatched by CodeIslandCustomElement.mountStyles
  // when a <link> stylesheet finishes loading. The event is composed + bubbles so
  // it crosses the shadow DOM boundary and reaches the iframe document.
  const handleStylesheetLoaded = (event: Event) => {
    const sheet = (event as CustomEvent).detail?.sheet as
      | CSSStyleSheet
      | undefined;
    if (sheet) {
      processStylesheet(sheet);
    }
  };
  doc.addEventListener(
    'wf:code-island-stylesheet-loaded',
    handleStylesheetLoaded
  );

  return () => {
    doc.removeEventListener(
      'wf:code-island-stylesheet-loaded',
      handleStylesheetLoaded
    );
  };
}

/**
 * Can be a CSSStyleSheet or a CSSGroupingRule.
 */
interface HasCSSRuleList {
  cssRules: CSSRuleList;
  deleteRule(index: number): void;
  insertRule(rule: string, index?: number): void;
}

type RuleInfo = {
  rule: CSSMediaRule;
  parent: HasCSSRuleList;
};

function isWebflowStylesheet(sheet: CSSStyleSheet) {
  return (sheet as any).__wf_sheet;
}

/**
 * Returns true for the built-in designer canvas stylesheet (bootflow/normalize)
 * loaded via `<link>` or `@import`. These stylesheets may contain their own
 * `@container` and `@media` rules that do not need to be replaced.
 */
function isBuiltinStylesheet(sheet: CSSStyleSheet) {
  const ownerNode = sheet.ownerNode;
  if (ownerNode && 'hasAttribute' in ownerNode) {
    return ownerNode.hasAttribute('data-wf-builtin-stylesheet');
  }
  return false;
}

function getBreakpointContainerName(breakpoint: Breakpoint) {
  return prependContainerName(breakpoint.id);
}

/**
 * Rewrites viewport-unit declarations in place while preserving CSSOM priority
 * and skipping inaccessible sheets.
 *
 * Mutating declarations keeps live CSSOM rule identity/order intact, unlike
 * reparsing and replacing full stylesheet text.
 */
function rewriteViewportUnitsInSheet(sheet: CSSStyleSheet) {
  try {
    rewriteViewportUnitsInRules(sheet.cssRules);
  } catch {
    // Stylesheet may not be accessible (cross-origin)
  }
}

function rewriteViewportUnitsInRules(rules: CSSRuleList) {
  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i]!;

    if (isCSSStyleRule(rule)) {
      rewriteViewportUnitsInRule(rule);
    }
    if (isCSSGroupingRule(rule)) {
      rewriteViewportUnitsInRules(rule.cssRules);
    }
  }
}

function rewriteViewportUnitsInRule(rule: CSSStyleRule) {
  for (let i = 0; i < rule.style.length; i++) {
    const prop =
      typeof rule.style.item === 'function'
        ? rule.style.item(i)
        : rule.style[i];
    if (!prop) continue;
    const value = rule.style.getPropertyValue(prop);
    const priority = rule.style.getPropertyPriority(prop);
    const rewritten = rewriteViewportUnits(value, 'value');

    if (rewritten !== value) {
      rule.style.setProperty(prop, rewritten, priority);
    }
  }
}

/**
 * Converts `@media` rules in a stylesheet to `@container` rules.
 *
 * Will replace a single `@media` rule with N `@container` rules, where N is the
 * number of unique names in `containerNames`.
 *
 * Note: Not all media queries can be converted to container queries (e.g., those
 * using non-dimensional features like hover, prefers-color-scheme, etc.). Failed
 * conversions are logged via reportBug and skipped gracefully.
 */
function mediaToContainerQueries(
  sheet: CSSStyleSheet,
  containerNames: Set<string>,
  bodyElementStyleTarget?: string | null
) {
  let rulesToAttemptToReplace: RuleInfo[] = [];
  try {
    rulesToAttemptToReplace = collectMediaRules(
      sheet.cssRules,
      null,
      bodyElementStyleTarget
    );
  } catch (e) {
    return;
  }

  for (const {rule, parent} of rulesToAttemptToReplace) {
    const originalCondition = rule.conditionText || rule.media.mediaText;

    const condition =
      normalizeMediaConditionForContainerQuery(originalCondition);

    // Skip if the condition is empty after stripping (e.g., "@media screen")
    if (!condition.trim()) {
      continue;
    }

    const ruleContent = extractRuleContent(rule);
    /**
     * Since we have potentially multiple container names, each of which should
     * essentially have a copy of the media rule we're replacing, we group all
     * of them into a single `@media all {}` rule. This makes it easier to
     * ensure that the order of rules is preserved, as we only ever replace the
     * original rule with a single `@media all {}` rule, thereby keeping `index`
     * the same and not changing the number of rules in the parent.
     */
    const containerRule = replaceRule(parent, rule, `@media all {}`);
    if (!containerRule) continue;
    if (!isCSSMediaRule(containerRule)) continue;

    for (const containerName of containerNames.values()) {
      try {
        containerRule.insertRule(
          `@container ${containerName} ${condition} {${ruleContent}}`
        );
      } catch (e) {
        // Catch and report any errors thrown by container rule insertions to ensure
        // invalid CSS in custom code blocks don't crash the component canvas.
        reportBug(
          '[ComponentCanvas][mediaToContainerQueries]',
          `Failed to convert media query to container query.`,
          {
            originalCondition,
            convertedCondition: condition,
            team: 'structure',
            ticket: 'https://webflow.atlassian.net/browse/STRUCT-3415',
          }
        );
      }
    }
  }
}

/**
 * Disables `@media` rules in a builtin stylesheet when a matching `@container`
 * rule already exists in the same sheet, by setting their `media.mediaText` to
 * `not all`. The original `mediaText` is recorded in
 * `mut_disabledBuiltinMediaRules` so it can be restored when Open Canvas
 * deactivates (see `CanvasBody.tsx`).
 *
 * `@media` and `@container` are independent, additive at-rules, not an
 * override pair: in Open Canvas, the shared site iframe's window can be
 * narrower than a wide frame inside it, making a builtin `@media` rule (e.g.
 * navbar collapse) apply even though its `@container` equivalent doesn't.
 * Disabling the redundant `@media` rule fixes that. It can't be deleted
 * outright, since outside Open Canvas there's no `@container` ancestor and
 * the `@media` rule needs to keep working there.
 */
function disableMediaRulesWithExistingContainerEquivalent(
  sheet: CSSStyleSheet,
  mut_disabledBuiltinMediaRules: Map<CSSMediaRule, string>
) {
  let containerRuleContents: Set<string>;
  try {
    containerRuleContents = collectContainerRuleContents(sheet.cssRules);
  } catch {
    return;
  }
  if (containerRuleContents.size === 0) return;

  let mediaRules: RuleInfo[];
  try {
    mediaRules = collectMediaRules(sheet.cssRules);
  } catch {
    return;
  }

  for (const {rule} of mediaRules) {
    if (mut_disabledBuiltinMediaRules.has(rule)) continue;
    if (!containerRuleContents.has(extractRuleContent(rule))) continue;

    mut_disabledBuiltinMediaRules.set(rule, rule.media.mediaText);
    rule.media.mediaText = 'not all';
  }
}

/**
 * Recursively collects the inner declaration text (via {@link
 * extractRuleContent}) of every `@container` rule in `rules`.
 */
function collectContainerRuleContents(
  rules: CSSRuleList,
  mut_result: Set<string> = new Set()
): Set<string> {
  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i]!;
    if (isCSSContainerRule(rule)) {
      mut_result.add(extractRuleContent(rule));
    }
    if (isCSSGroupingRule(rule)) {
      collectContainerRuleContents(rule.cssRules, mut_result);
    }
  }
  return mut_result;
}

/**
 * Replaces an old rule with a new rule in a stylesheet. Maintains the original
 * order of rules. Returns the new rule.
 */
function replaceRule(
  parent: HasCSSRuleList,
  oldRule: CSSRule,
  newRule: string
) {
  const index = [...parent.cssRules].indexOf(oldRule);
  if (index === -1) return;
  parent.deleteRule(index);
  parent.insertRule(newRule, index);
  return parent.cssRules[index];
}

/**
 * Recursively collect all `@media` rules into the provided `mut_collection`
 * parameter. This parameter is mutated.
 */
function collectMediaRules(
  rules: CSSRuleList,
  parent: CSSGroupingRule | null = null,
  bodyElementStyleTarget?: string | null
) {
  const result: RuleInfo[] = [];
  if (!rules) return result;

  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i]!;

    // We ignore `@media all {}` rules, as they are not actually conditional and
    // more importantly they are _probably_ rules we created to replace the
    // original `@media` rule from a previous run of
    // `replaceComponentCanvasMediaQueries`.
    if (isCSSMediaRule(rule) && rule.conditionText !== 'all') {
      result.push({
        rule,
        parent: parent ?? rule.parentStyleSheet!,
      });
      rewriteBodySelectorsInRules(rule.cssRules, bodyElementStyleTarget);
    } else {
      // A CSSStyleRule can also be a CSSGroupingRule via CSS Nesting, so
      // these checks are intentionally not mutually exclusive: rewrite the
      // selector here AND still recurse into any nested rules so nested
      // `@media` inside a style rule still gets converted to `@container`.
      if (isCSSStyleRule(rule)) {
        rewriteBodySelectorRule(rule, bodyElementStyleTarget);
      }
      if (isCSSGroupingRule(rule)) {
        // Recursively check nested rules (e.g., within @supports or CSS Nesting)
        result.push(
          ...collectMediaRules(rule.cssRules, rule, bodyElementStyleTarget)
        );
      }
    }
  }
  return result;
}

function rewriteBodySelectorsInRules(
  rules: CSSRuleList,
  bodyElementStyleTarget?: string | null
) {
  if (!bodyElementStyleTarget) return;

  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i]!;

    // A CSSStyleRule can also be a CSSGroupingRule via CSS Nesting, so
    // these checks are intentionally not mutually exclusive: rewrite the
    // selector here AND still descend into any nested children.
    if (isCSSStyleRule(rule)) {
      rewriteBodySelectorRule(rule, bodyElementStyleTarget);
    }
    if (isCSSGroupingRule(rule)) {
      rewriteBodySelectorsInRules(rule.cssRules, bodyElementStyleTarget);
    }
  }
}

function rewriteBodySelectorRule(
  rule: CSSStyleRule,
  bodyElementStyleTarget?: string | null
) {
  if (!bodyElementStyleTarget) return;

  const rewrittenSelectorText = rewriteBodySelectors(
    rule.selectorText,
    bodyElementStyleTarget
  );

  if (!rewrittenSelectorText || rewrittenSelectorText === rule.selectorText) {
    return;
  }

  try {
    rule.selectorText = rewrittenSelectorText;
  } catch {
    reportBug(
      '[OpenCanvas][rewriteBodySelectorRule]',
      `Failed to rewrite body selector for Open Canvas.`,
      {
        selectorText: rule.selectorText,
        rewrittenSelectorText,
        team: 'design-experience',
        ticket: 'https://webflow.atlassian.net/browse/DEX-544',
      }
    );
  }
}

/**
 * Extract the content of a rule (all nested rules as text)
 */
function extractRuleContent(rule: CSSGroupingRule) {
  let content = '';
  if (rule.cssRules) {
    for (let i = 0; i < rule.cssRules.length; i++) {
      content += rule.cssRules[i]!.cssText;
    }
  }
  return content;
}

function isCSSGroupingRule(rule: CSSRule): rule is CSSGroupingRule {
  return 'cssRules' in rule && 'insertRule' in rule && 'deleteRule' in rule;
}

function isCSSMediaRule(rule: CSSRule): rule is CSSMediaRule {
  return rule.constructor.name === 'CSSMediaRule';
}

function isCSSContainerRule(rule: CSSRule): rule is CSSGroupingRule {
  return rule.constructor.name === 'CSSContainerRule';
}

function isCSSStyleRule(rule: CSSRule): rule is CSSStyleRule {
  return rule.constructor.name === 'CSSStyleRule';
}

/**
 * A size container query that always matches, used where a `@media` condition
 * is unconditionally true on the component canvas.
 */
const ALWAYS_TRUE_CONTAINER_CONDITION = '(width >= 0px)';

/**
 * Tailwind v4 (and similar) emits `@media not all and (min-width: N…)` for
 * `max-[N…]:` utilities, which Chrome reports back in range syntax as
 * `not all and (width >= N…)`. Browsers treat that like `not (min-width: N…)`, i.e.
 * true when the viewport width is **strictly less than** N (not `max-width: N`,
 * which would include exactly N).
 *
 * Container size queries support the same range syntax, so we map to
 * `(width < N…)` for strict equivalence. `(max-width: calc(N - 1px))` would
 * still disagree with sub-pixel sizes; `width < N` matches the media math.
 *
 * Returns `null` when the condition does not match this pattern.
 */
function tryConvertTailwindNotMinWidth(mediaCondition: string): string | null {
  const match = mediaCondition.match(
    /^\s*not\s+(?:all|screen)\s+and\s+\(\s*(?:min-width\s*:\s*([\d.]+)\s*(px|em|rem)|width\s*>=\s*([\d.]+)\s*(px|em|rem))\s*\)\s*$/i
  );
  if (!match) {
    return null;
  }
  const [, minWidthValue, minWidthUnit, rangeValue, rangeUnit] = match;
  const breakpoint = minWidthValue ?? rangeValue;
  const unit = minWidthUnit ?? rangeUnit;
  return `(width < ${breakpoint}${unit})`;
}

/**
 * `not <type> and <rest>` means `not (<type> and <rest>)`. On the component
 * canvas `all` and `screen` always match, so the negation reduces to
 * `not (<rest>)`, which container queries support directly.
 *
 * This exists because dropping it is catastrophic rather than merely lossy:
 * `stripMediaTypePrefix` removes `not` along with the media type, turning
 * `not all and (width >= 768px)` into `(width >= 768px)` — the exact inverse.
 * Every Tailwind `max-[N]:` utility then applies at every width ≥ N instead of
 * below it, so a component renders as though the viewport were at its smallest
 * breakpoint (EXT-1989).
 *
 * Returns `null` when the condition is not a `not <type> and …` form.
 */
function tryPreserveNotNegation(mediaCondition: string): string | null {
  const match = mediaCondition.match(
    /^\s*not\s+(all|screen|print|speech)\s+and\s+(.+?)\s*$/i
  );
  if (!match) {
    return null;
  }
  // Both groups are required by the pattern, so the fallbacks are unreachable;
  // an empty media type would fall to the always-true branch rather than the
  // negation-dropping one, which is the safe direction.
  const mediaType = match[1] ?? '';
  const rest = match[2] ?? '';
  // `not <type> and <rest>` is `not (<type> and <rest>)`. The component canvas
  // is a screen surface, so `all` and `screen` both match and the negation
  // reduces to `not (<rest>)`. `print` and `speech` never match here, which
  // makes the whole condition unconditionally true instead — emitting
  // `not (<rest>)` for those would invert them in the other direction.
  return /^(?:all|screen)$/i.test(mediaType)
    ? `not (${rest})`
    : ALWAYS_TRUE_CONTAINER_CONDITION;
}

/**
 * A size container query that never matches, used where a `@media` condition is
 * unconditionally false on the component canvas.
 */
const NEVER_TRUE_CONTAINER_CONDITION = '(width < 0px)';

/**
 * `print` and `speech` never match on the component canvas, so `print and
 * <rest>` is unconditionally false however `<rest>` evaluates.
 *
 * The negated direction is handled by `tryPreserveNotNegation`. Without this the
 * positive direction falls through to `stripMediaTypePrefix`, which drops the
 * media type and leaves `(<rest>)` — so a print-only rule such as
 * `display: none` starts applying on the canvas whenever the container matches
 * `<rest>`. That is the mirror of the inversion this module exists to prevent.
 *
 * Returns `null` when the condition does not lead with a non-matching type.
 */
function tryHandleNonMatchingMediaType(mediaCondition: string): string | null {
  const match = mediaCondition.match(
    /^\s*(?:only\s+)?(?:print|speech)(?:\s+and\s+.+)?\s*$/i
  );
  return match ? NEVER_TRUE_CONTAINER_CONDITION : null;
}

/**
 * Splits a media query list into its trimmed queries. A comma inside
 * parentheses, such as in `min(500px, 50vw)`, doesn't split.
 *
 * @example
 * splitMediaQueryList('screen and (max-width: 991px), not all, (min-width: min(500px, 50vw))')
 * // Returns: ['screen and (max-width: 991px)', 'not all', '(min-width: min(500px, 50vw))']
 */
function splitMediaQueryList(condition: string): string[] {
  const queries: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < condition.length; i++) {
    const character = condition[i];
    if (character === '(') depth++;
    else if (character === ')') depth = Math.max(0, depth - 1);
    else if (character === ',' && depth === 0) {
      queries.push(condition.slice(start, i).trim());
      start = i + 1;
    }
  }
  queries.push(condition.slice(start).trim());
  return queries;
}

/**
 * The container condition for each query that is only a media type, such as
 * `screen` or `not all`.
 */
const BARE_MEDIA_TYPE_CONDITIONS = new Map<string, string>([
  // Always true.
  ['all', ALWAYS_TRUE_CONTAINER_CONDITION],
  ['screen', ALWAYS_TRUE_CONTAINER_CONDITION],
  ['not print', ALWAYS_TRUE_CONTAINER_CONDITION],
  ['not speech', ALWAYS_TRUE_CONTAINER_CONDITION],

  // Never true.
  ['not all', NEVER_TRUE_CONTAINER_CONDITION],
  ['not screen', NEVER_TRUE_CONTAINER_CONDITION],
  ['print', NEVER_TRUE_CONTAINER_CONDITION],
  ['speech', NEVER_TRUE_CONTAINER_CONDITION],
]);

/**
 * Returns the container condition for a query that is only a media type, or
 * `null` when the query has a condition.
 */
function getBareMediaTypeCondition(query: string): string | null {
  const words = query.trim().toLowerCase().split(' ').filter(Boolean);
  // `only` doesn't change what a query matches.
  if (words[0] === 'only') {
    words.shift();
  }
  return BARE_MEDIA_TYPE_CONDITIONS.get(words.join(' ')) ?? null;
}

/**
 * The features an `inline-size` frame can evaluate. A condition with any other
 * feature never matches on a frame, even inside an `or`.
 */
const CONTAINER_WIDTH_FEATURES = new Set([
  'width',
  'min-width',
  'max-width',
  'inline-size',
  'min-inline-size',
  'max-inline-size',
]);

/**
 * The characters of the range operators in `(400px < width <= 700px)`.
 */
const RANGE_OPERATOR_CHARACTERS = ['<', '>', '='];

/**
 * Returns the lowercased feature name inside one pair of parentheses, or an
 * empty string when there is none.
 *
 * @example
 * getFeatureName('max-width: 480px') // Returns: 'max-width'
 * getFeatureName('400px < width < 700px') // Returns: 'width'
 * getFeatureName('500px, 50vw') // Returns: '' (arguments of `min(…)`)
 */
function getFeatureName(feature: string): string {
  const colonIndex = feature.indexOf(':');
  if (colonIndex !== -1) {
    return feature.slice(0, colonIndex).trim().toLowerCase();
  }
  let parts = [feature];
  for (const operator of RANGE_OPERATOR_CHARACTERS) {
    parts = parts.flatMap((part) => part.split(operator));
  }
  // In a range, the feature name is the only part that isn't a number.
  const name = parts
    .map((part) => part.trim())
    .find((part) => part !== '' && Number.isNaN(parseFloat(part)));
  return name?.toLowerCase() ?? '';
}

/**
 * Returns the feature names in a condition, read from its innermost
 * parentheses.
 *
 * @example
 * getFeatureNames('(min-width: 768px) and (400px < width < 700px)')
 * // Returns: ['min-width', 'width']
 */
function getFeatureNames(condition: string): string[] {
  const names: string[] = [];
  let groupStart = -1;
  for (let i = 0; i < condition.length; i++) {
    const character = condition[i];
    if (character === '(') {
      groupStart = i + 1;
    } else if (character === ')' && groupStart !== -1) {
      const name = getFeatureName(condition.slice(groupStart, i));
      if (name) {
        names.push(name);
      }
      groupStart = -1;
    }
  }
  return names;
}

/**
 * Returns true when every feature in the condition is in
 * `CONTAINER_WIDTH_FEATURES`.
 */
function isConditionUsingOnlyWidthFeatures(condition: string): boolean {
  return getFeatureNames(condition).every((name) =>
    CONTAINER_WIDTH_FEATURES.has(name)
  );
}

/**
 * Returns true when the condition is one feature in parentheses, such as
 * `(max-width: 480px)`.
 */
function isSingleFeature(condition: string): boolean {
  return (
    condition.startsWith('(') &&
    condition.endsWith(')') &&
    condition.indexOf('(', 1) === -1
  );
}

/**
 * Converts one media query to a container condition.
 */
function normalizeMediaQuery(query: string): string {
  return (
    tryConvertTailwindNotMinWidth(query) ??
    tryPreserveNotNegation(query) ??
    tryHandleNonMatchingMediaType(query) ??
    stripMediaTypePrefix(query)
  );
}

/**
 * Converts the queries of a `@media` list into one `@container` condition.
 * Converts each query, drops the ones that never match or use anything but
 * width, wraps compound queries in parentheses, and joins the rest with `or`.
 * Returns the never-true condition when no query is left.
 */
function normalizeMediaQueryList(queries: string[]): string {
  const operands: string[] = [];
  for (const query of queries) {
    const condition =
      getBareMediaTypeCondition(query) ?? normalizeMediaQuery(query);
    // Drops queries that never match on a frame.
    if (
      condition === NEVER_TRUE_CONTAINER_CONDITION ||
      !isConditionUsingOnlyWidthFeatures(condition)
    ) {
      continue;
    }
    // Wraps each compound query so it can be an `or` operand.
    operands.push(isSingleFeature(condition) ? condition : `(${condition})`);
  }
  return operands.length > 0
    ? operands.join(' or ')
    : NEVER_TRUE_CONTAINER_CONDITION;
}

/**
 * Prepares a `@media` condition string for use inside `@container …`.
 * Handles the Tailwind `not <type> and (min-width: …)` / `(width >= …)` shorthand
 * first, then any other `not <type> and …` form, and only then removes media type
 * prefixes as container queries don't support media types (screen, print, etc.)
 * or the "only" keyword.
 *
 * Order matters: `stripMediaTypePrefix` deletes a leading `not`, so anything
 * negated must be converted before it reaches that fall-through.
 */
export function normalizeMediaConditionForContainerQuery(
  originalCondition: string
): string {
  // Converts each query of a list on its own.
  const queries = splitMediaQueryList(originalCondition);
  if (queries.length > 1) {
    return normalizeMediaQueryList(queries);
  }
  return normalizeMediaQuery(originalCondition);
}

/**
 * Strips media type prefixes from a media condition to make it compatible
 * with container queries.
 *
 * Container queries don't support media types (screen, print, etc.) or
 * keywords like "only" and "not", so these must be removed.
 *
 * @example
 * stripMediaTypePrefix('only screen and (max-width: 480px)')
 * // Returns: '(max-width: 480px)'
 */
export function stripMediaTypePrefix(condition: string): string {
  return condition.replace(
    /^\s*(not\s+)?(only\s+)?(all|screen|print|speech)(\s+and)?\s*/i,
    ''
  );
}

export const forTestSuite = {
  ALWAYS_TRUE_CONTAINER_CONDITION,
  NEVER_TRUE_CONTAINER_CONDITION,
  splitMediaQueryList,
  getBareMediaTypeCondition,
  getFeatureName,
  getFeatureNames,
  isConditionUsingOnlyWidthFeatures,
  isSingleFeature,
  normalizeMediaQuery,
  normalizeMediaQueryList,
};
